// src/contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../config/supabase'; // CORRECT: Import depuis src/

import { normalizeRegistrationEmail, validateRegistrationEmail, validateRegistrationPassword } from '../utils/registrationValidation';

const AuthContext = createContext();
const STAFF_SESSION_KEY = 'nsara_staff_session';

const normalizeEmailInput = (value) => String(value || '').trim().toLowerCase();
const normalizePasswordInput = (value) => String(value || '');
const resolveDisplayName = (user) =>
  String(
    user?.user_metadata?.nom ||
    user?.user_metadata?.name ||
    user?.displayName ||
    user?.email ||
    ''
  ).trim();

// SECURITY (Mission 01 / Step 4): a legacy client-only "staff login" fallback
// used to live here. It matched a plaintext password against a `personnel`
// array stored in this browser's localStorage and, on match, issued a staff
// session carrying uid: ownerUid — the real business owner's uid. Because
// apiClient's Authorization header is taken from whatever Firebase session
// happens to be cached (see config/supabase.js), a "staff" login on a device
// where the owner's real session was still cached silently inherited the
// owner's full backend authority, regardless of the (never server-enforced)
// permissions shown in PersonnelModule. There is no backend model for staff
// accounts/roles today, so this fallback has been removed rather than kept
// as a false sense of access control. See the Mission 01 audit report.
export function useAuth() {  // AJOUT: Exportez useAuth
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {  // AJOUT: Exportez AuthProvider
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [INTERNAL_DEV_MODE] = useState(false);

  useEffect(() => {
    // Écouter les changements d'authentification Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Supabase Auth Event:', event);
        
        if (session?.user) {
          const userData = {
            uid: session.user.id,
            email: session.user.email,
            displayName: resolveDisplayName(session.user),
            photoURL: session.user.user_metadata?.avatar_url,
            // P-AUTH-EMAIL-VERIFY-2: Firebase's own signal, carried through
            // as-is from the session (see buildSessionFromFirebaseUser /
            // hydrateSessionProfile in config/supabase.js) — never inferred
            // from provider name here.
            emailVerified: Boolean(session.user.emailVerified),
          };
          localStorage.removeItem(STAFF_SESSION_KEY);
          setCurrentUser(userData);
        } else {
          localStorage.removeItem(STAFF_SESSION_KEY); // clears any stale session from before the Mission 01 fix
          setCurrentUser(null);
        }
        setLoading(false);
      }
    );

    // Vérifier la session actuelle
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const userData = {
          uid: session.user.id,
          email: session.user.email,
          displayName: resolveDisplayName(session.user),
          photoURL: session.user.user_metadata?.avatar_url,
          emailVerified: Boolean(session.user.emailVerified),
        };
        setCurrentUser(userData);
      } else {
        localStorage.removeItem(STAFF_SESSION_KEY); // clears any stale session from before the Mission 01 fix
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fonction de login avec email/mot de passe
  const login = async (email, password) => {
    const safeEmail = normalizeEmailInput(email);
    const safePassword = normalizePasswordInput(password);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: safeEmail,
        password: safePassword,
      });

      if (error) {
        console.error('Login error:', error);
        throw error;
      }

      return data.user;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  // Fonction de login avec Google
  const loginWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      });

      if (error) {
        console.error('Google login error:', error);
        throw error;
      }
    } catch (error) {
      console.error('Google login failed:', error);
      throw error;
    }
  };

  // Connexion sans mot de passe: envoie un lien de connexion à l'adresse
  // fournie. Additif — n'affecte pas login()/signup() ci-dessus, qui
  // restent disponibles pendant la transition (voir Login.js).
  const sendMagicLink = async (email) => {
    const safeEmail = normalizeEmailInput(email);
    const { error } = await supabase.auth.signInWithOtp({ email: safeEmail });
    if (error) throw error;
  };

  // À appeler au montage de la page qui reçoit le lien (voir Login.js):
  // vrai si l'URL courante EST un lien de connexion à compléter.
  const isMagicLinkUrl = () => supabase.auth.isSignInWithEmailLink();

  const completeMagicLinkSignIn = async () => {
    const { data, error } = await supabase.auth.completeEmailLinkSignIn();
    if (error) throw error;
    return data?.user;
  };
  // Fonction d'inscription
  const signup = async ({ email, password, name } = {}) => {
    const safeEmail = normalizeRegistrationEmail(email);
    const safePassword = normalizePasswordInput(password);
    const validationError = validateRegistrationEmail(safeEmail) || validateRegistrationPassword(safePassword);
    if (validationError) throw new Error(validationError);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: safeEmail,
        password: safePassword,
        options: {
          data: {
            name: name,
          },
        },
      });

      if (error) {
        console.error('Signup error:', error);
        throw error;
      }

      return data.user;
    } catch (error) {
      console.error('Signup failed:', error);
      throw error;
    }
  };

  // Fonction de déconnexion
  const logout = async () => {
    try {
      if (currentUser?.isStaff) {
        localStorage.removeItem(STAFF_SESSION_KEY);
      } else {
        await supabase.auth.signOut();
      }
      setCurrentUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  // Fonction de réinitialisation de mot de passe
  const resetPassword = async (email) => {
    const safeEmail = normalizeEmailInput(email);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(safeEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  // P-AUTH-EMAIL-VERIFY-2: re-checks the current session's own Firebase
  // account state (never an arbitrary email) — used by "J'ai vérifié mon
  // adresse" to observe a verification link the person already clicked.
  // Returns the fresh boolean directly; currentUser.emailVerified updates
  // via the same onAuthStateChange listener that already reacts to every
  // other auth event, so callers don't need to do anything else with it.
  const refreshEmailVerification = async () => {
    try {
      const { data, error } = await supabase.auth.refreshUserProfile();
      if (error) {
        console.error('Refresh verification error:', error);
        throw error;
      }
      return Boolean(data?.session?.user?.emailVerified);
    } catch (error) {
      console.error('Refresh verification failed:', error);
      throw error;
    }
  };

  // Resends the verification e-mail for whoever is CURRENTLY authenticated
  // — see sendEmailVerification in config/supabase.js: identity comes from
  // the live session's idToken, never a user-supplied address.
  const resendEmailVerification = async () => {
    try {
      const { error } = await supabase.auth.sendEmailVerification();
      if (error) {
        console.error('Resend verification error:', error);
        throw error;
      }
    } catch (error) {
      console.error('Resend verification failed:', error);
      throw error;
    }
  };

  const updateProfile = async ({ nom, photoURL } = {}) => {
    if (!currentUser || currentUser?.isStaff) {
      return { success: false, message: 'Profil non modifiable pour ce compte.' };
    }
    const nextNom = String(nom || '').trim();
    const nextPhotoURL = typeof photoURL === 'string' ? photoURL.trim() : undefined;
    const updatePayload = {
      data: {
        nom: nextNom,
        name: nextNom,
        ...(nextPhotoURL !== undefined ? { photoURL: nextPhotoURL } : {})
      }
    };

    let { data, error } = await supabase.auth.updateUser(updatePayload);
    const normalizedError = String(error?.message || error?.code || '').toUpperCase();

    if (error && (normalizedError.includes('INVALID_ID_TOKEN') || normalizedError.includes('JWT'))) {
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        throw new Error(`Session expirée (${refreshError.message}). Veuillez vous reconnecter.`);
      }
      const retry = await supabase.auth.updateUser(updatePayload);
      data = retry.data;
      error = retry.error;
    }

    if (error) throw error;

    setCurrentUser((prev) => ({
      ...prev,
      displayName: nextNom || prev?.displayName || prev?.email || '',
      ...(nextPhotoURL !== undefined ? { photoURL: nextPhotoURL } : {})
    }));
    return { success: true, user: data?.user || null };
  };

  const value = {
    currentUser,
    login,
    loginWithGoogle,
    signup,
    logout,
    resetPassword,
    updateProfile,
    sendMagicLink,
    isMagicLinkUrl,
    completeMagicLinkSignIn,
    refreshEmailVerification,
    resendEmailVerification,
    loading,
    INTERNAL_DEV_MODE,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// PAS de export default ici - seulement des exports nommés
